import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, Checkbox, Container, Tooltip, Typography } from "@mui/material"
import { useApiPut } from "auth/useApi2"
import { useApi } from "auth/useApi3"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import { MultiSelectFilter } from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import TableLink from "components/ClpTable/components/actions/TableLink/TableLink"
import { StyledBoldTypography } from "components/ClpTable/components/typography/typography.styled"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import ChipInfoIcon from "components/Status/ChipInfo/ChipInfoIcon"
import { ChipInfoStatusEnum } from "components/Status/ChipInfo/chipInfo.interface"
import theme from "css/theme/light"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoutes } from "generated/routes"
import moment from "moment"
import { useContext, useEffect, useState } from "react"
import { StyledTableContainer } from "shared/styled/Global"
import { StyledIconButton } from "./components/VehicleAlarmComment/VehicleAlarmComment.styled"
import { VehicleAlarmDashboard } from "./components/VehicleAlarmDashboard"
import { VehicleBar } from "./components/VehicleBar/VehicleBar"
import { useTranslation } from "react-i18next"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"
import { id } from "date-fns/locale"

const statusString = (value, t) => {
  switch (value) {
    case 1:
      return t("global.ongoing", "Ongoing")
    case 2:
      return t("global.completed", "Completed")
    default:
      return t("global.active", "Active")
  }
}

const status = (value) => {
  switch (value) {
    case 0:
      return <ChipInfoIcon type={ChipInfoStatusEnum.Active} />
    case 1:
      return <ChipInfoIcon type={ChipInfoStatusEnum.Booked} />
    case 2:
      return <ChipInfoIcon type={ChipInfoStatusEnum.Completed} />
    default:
      return <ChipInfoIcon type={ChipInfoStatusEnum.Active} />
  }
}

const alarmType = (value, t) => {
  switch (value) {
    case 1:
      return t("vehiclealarm.alcohol_lock", "Alcohol lock")
    default:
      return t("vehiclealarm.drivers_cards", "Drivers card")
  }
}

const VehicleAlarm = () => {
  const { t } = useTranslation()
  const loadingContext = useContext(GlobalLoaderContext)
  const appContext = useContext(AppContext)
  const [formData, setFormData] = useState()
  const [alarmData, setAlarmData] = useState({
    total: 0,
    open: 0,
    ongoing: 0,
    done: 0
  })
  const [selectedAlarmIds, setSelectedAlarmIds] = useState([])
  const [vehicleAlarmsResult, vehiclesAlarmsFuncs] = useApi([])

  useEffect(() => {
    vehiclesAlarmsFuncs.get(apiEndpoints.vehiclealarm.getvehiclealarm)
  }, [])

  useEffect(() => {
    loadingContext.setLoading(() => vehicleAlarmsResult.status === 1)
  })

  useEffect(() => {
    calculateData(vehicleAlarmsResult)
  }, [vehicleAlarmsResult])

  const getFormData = (row) => {
    setFormData(row)
    appContext.toggleSideBar(true)
  }
  useEffect(() => {
    appContext.toggleSideBar("VehicleAlarm", selectedAlarmIds.length > 0)
  }, [selectedAlarmIds])

  useEffect(() => {
    if (vehicleAlarmsResult.status !== 2) return
    if (selectedAlarmIds.length === 1) {
      const selectedRow = vehicleAlarmsResult.data?.find((row) => row.id === selectedAlarmIds[0])
      if (selectedRow) {
        setFormData(selectedRow) // Update form with selected row data
      }
    }
  }, [selectedAlarmIds, vehicleAlarmsResult])

  const calculateData = (vehicleAlarmsResult) => {
    if (vehicleAlarmsResult.data != null) {
      setAlarmData({
        total: vehicleAlarmsResult.data.length,
        open: vehicleAlarmsResult.data.filter((v) => v.status === 0).length,
        ongoing: vehicleAlarmsResult.data.filter((v) => v.status === 1).length,
        done: vehicleAlarmsResult.data.filter((v) => v.status === 2).length
      })
    }
  }

  const [updateVehicleResult, updateVehicle] = useApiPut()
  const handleSubmit = (values) => {
    console.log("selectedAlarmIds", selectedAlarmIds)
    if (selectedAlarmIds.length > 0) {
      const payload = selectedAlarmIds.map((id) => ({
        id,
        status: values.status,
        measure: values.measure,
        userId: values.userId
      }))
      updateVehicle(apiEndpoints.vehiclealarm.updatevehiclealarmbyid, payload)
    } else {
      const payload = [
        {
          id: values.id,
          status: values.status,
          measure: values.measure,
          userId: values.userId
        }
      ]
      updateVehicle(apiEndpoints.vehiclealarm.updatevehiclealarmbyid, payload)
    }

    selectedAlarmIds.forEach((id) => {
      const index = vehicleAlarmsResult.data.findIndex((item) => item.id === id)
      if (index !== -1) {
        vehicleAlarmsResult.data[index] = {
          ...vehicleAlarmsResult.data[index],
          ...values
        }
      }
    })
    appContext.toggleSideBar("VehicleAlarm", false)
    calculateData(vehicleAlarmsResult)
  }

  useEffect(() => {
    if (updateVehicleResult.status !== 2) return
    vehiclesAlarmsFuncs.get(apiEndpoints.vehiclealarm.getvehiclealarm)
    setSelectedAlarmIds([])
  }, [updateVehicleResult])

  const handleCheckboxChange = (id) => {
    setSelectedAlarmIds((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]))
  }

  const columns = [
    {
      accessorKey: "checkbox",
      header: "",
      cell: ({ cell }) => {
        const rowId = cell.row.original.id
        return (
          <Box sx={{ width: "1px" }}>
            <Checkbox
              checked={selectedAlarmIds.includes(rowId)}
              color="primary"
              onClick={(e) => e.stopPropagation()}
              onChange={() => handleCheckboxChange(rowId)}
            />
          </Box>
        )
      }
    },
    {
      accessorKey: "measure",
      header: t("global.measure", "Measure"),
      cell: ({ cell }) => {
        return (
          <Tooltip
            title={
              cell?.row?.original?.measure
                ? cell.row.original.measure
                : "Det finns för tillfället ingen åtgärd"
            }>
            <StyledIconButton size="small">
              <FontAwesomeIcon size={"1x"} icon={faInfoCircle} color={theme.palette.primary[700]} />
            </StyledIconButton>
          </Tooltip>
        )
      }
    },
    {
      accessorKey: "vehicle.internnumber",
      header: t("global.table.internal_number", "Internal No")
    },
    {
      accessorKey: "vehicle.regNumber",
      header: t("global.table.regnr", "Reg no"),
      cell: ({ cell }) => {
        return (
          <TableLink
            label={cell.row.original.vehicle.regNumber}
            to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.vehicle.id)}
          />
        )
      }
    },
    {
      accessorFn: (row) => row.alarmType,
      id: "alarmType",
      cell: ({ cell }) => {
        return <StyledBoldTypography>{alarmType(cell.row.original.alarmType, t)}</StyledBoldTypography>
      },
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      header: t("vehiclealarm.alarm_type", "Alarm type"),
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter,
        filterHeader: t("vehiclealarm.alarm_type", "Alarm type"),
        filterOptions: [
          {
            name: t("vehiclealarm.alcohol_lock", "Alcohol lock"),
            value: 1
          },
          {
            name: t("vehiclealarm.drivers_cards", "Drivers card"),
            value: 2
          }
        ]
      }
    },

    {
      accessorFn: (row) => {
        return moment(row.alarmDate).isValid() && moment.utc(row.alarmDate).local().format("YYYY-MM-DD HH:mm")
      },
      header: t("vehiclealarm.alarm_time", "Alarm time")
    },

    {
      accessorFn: (row) => row.status,
      cell: ({ cell }) => {
        return status(cell.row.original.status)
      },
      header: t("global.status", "Status"),
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      meta: {
        mainFilter: true,
        filter: MultiSelectFilter,
        filterHeader: t("global.status", "Status"),
        filterOptions: [
          {
            name: t("global.active", "Active"),
            value: 0
          },
          {
            name: t("global.ongoing", "Ongoing"),
            value: 1
          },
          {
            name: t("global.completed", "Completed"),
            value: 2
          }
        ]
      }
    },
    {
      accessorKey: "user.name",
      header: t("global.responsible", "Responsible")
    },
    {
      accessorKey: "edit",
      header: "",
      meta: {
        align: "right"
      },
      cell: ({ cell }) => {
        return (
          <StyledTableButtonGroup>
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => {
                getFormData(cell.row.original)
              }}
            />
          </StyledTableButtonGroup>
        )
      }
    }
  ]

  return (
    <div>
      <Container>
        <VehicleBar
          formData={formData}
          onSubmit={handleSubmit}
          updateVehicleResult={updateVehicleResult}
          selectedAlarmIds={selectedAlarmIds}
          setSelectedAlarmIds={setSelectedAlarmIds}
        />
        <Box mb={5} sm={12} xs={12}>
          <Typography variant="h1">{t("vehiclealarm.vehiclealarm", "Vehicle alarm")}</Typography>
        </Box>
        <Box sm={12} xs={12}>
          <VehicleAlarmDashboard vehicleAlarmsResult={vehicleAlarmsResult} alarmData={alarmData} t={t} />
        </Box>
      </Container>
      <StyledTableContainer>
        <ClpTable
          columns={columns}
          rowProps={(row) => ({
            onClick: () => {
              setSelectedAlarmIds([row.original.id])
            }
          })}
          rows={vehicleAlarmsResult.status === 2 && vehicleAlarmsResult.data}
          loadingState={vehicleAlarmsResult.status}
          tableId={ClpTableEnum.VehicleAlarm}
        />
      </StyledTableContainer>
    </div>
  )
}

export default VehicleAlarm
